import { observer } from 'mobx-react'
import React, { ReactElement } from 'react'
import { useHistory } from 'react-router-dom'

import { CLOCK_IN_ROUTE_PATH } from '@flowwai-frontend/bps-officer-web/common/data-access'
import { ConfirmationModal } from '@flowwai-frontend/bps-officer-web/common/ui'
import {
   setClockInRedirectPath,
   useClockOut,
   useUserAppConfigStore
} from '@flowwai-frontend/bps-officer-web/iam'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import { useCustomT } from '@flowwai-frontend/shared/common-components'
import { Button, Hierarchy, SubVariant } from '@flowwai-frontend/design-system'
import { isAPIFetching } from '@flowwai-frontend/shared/api-common'
import { WavingIcon } from '@flowwai-frontend/design-system/icons'

import * as Styles from './styles'

interface Props {
   isClockOutModalOpen: boolean
   setIsClockOutModalOpen: (value: boolean) => void
}

const ClockOutController = (props: Props): ReactElement => {
   const { isClockOutModalOpen, setIsClockOutModalOpen } = props

   const userAppConfigStore = useUserAppConfigStore()

   const { theme } = useAppTheme()
   const clockOutAPIObj = useClockOut()
   const t = useCustomT('navbar:attendance.clockOut')

   const history = useHistory()
   const clockOutTheme = theme.navbar.attendance

   const onCloseModal = (): void => {
      setIsClockOutModalOpen(false)
   }

   const onSuccessClockOut = () => {
      if (userAppConfigStore.permissions.isAttendanceTrackingEnabled) {
         setClockInRedirectPath(history.location.pathname)
         history.replace(CLOCK_IN_ROUTE_PATH)
      }
      onCloseModal()
   }

   const onClickClockOut = (): void => {
      clockOutAPIObj.triggerAPI({
         onSuccess: onSuccessClockOut
      })
   }

   const renderConfirmButton = (): ReactElement => (
      <Button
         hierarchy={Hierarchy.Primary}
         subVariant={SubVariant.Primary}
         size={'Large'}
         onClick={onClickClockOut}
         className='flex-grow'
         isLoading={isAPIFetching(clockOutAPIObj.apiStatus)}
      >
         {t('clockOutConfirmText')}
      </Button>
   )

   const renderCancelButton = (): ReactElement => (
      <Button
         hierarchy={Hierarchy.Secondary}
         subVariant={SubVariant.GrayOutline}
         size={'Large'}
         onClick={onCloseModal}
         className='flex-grow'
      >
         {t('cancel')}
      </Button>
   )

   const renderConfirmationCard = (): ReactElement => (
      <div className='w-full'>
         <div className='p-32px'>
            <div className={Styles.titleAndDescriptionContainer}>
               <WavingIcon />
               <h3 className={Styles.getClockTitleStyles(clockOutTheme)}>
                  {t('title')}
               </h3>
               <p className={Styles.getClockDescriptionStyles(clockOutTheme)}>
                  {t('description')}
               </p>
            </div>
         </div>
         <div className={Styles.clockOutActionContainer}>
            {renderCancelButton()}
            {renderConfirmButton()}
         </div>
      </div>
   )

   return (
      <ConfirmationModal
         hideCloseIcon={true}
         isOpen={isClockOutModalOpen}
         apiStatus={clockOutAPIObj.apiStatus}
         modalContainerClassName='!w-[442px]'
         onCloseModal={onCloseModal}
         renderConfirmationCard={renderConfirmationCard}
      />
   )
}

export default observer(ClockOutController)
